import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setToken } from "../lib/api";
import { Card, Button, Input, ErrorNote } from "../components/ui";

/** Two steps, one screen: ask for the address, then ask for the code that was
 *  emailed to it. There is no password anywhere in this app. */
export default function SignIn() {
  const navigate = useNavigate();
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.auth.requestCode(email);
      setStep("code");
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { token } = await api.auth.verify(email, code);
      setToken(token);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <Card className="w-full max-w-sm p-6">
        <h1 className="text-lg font-semibold">Guest Feedback Desk</h1>
        <p className="mb-5 text-sm text-muted">
          {step === "email"
            ? "Enter your email and we'll send you a sign-in code."
            : `We sent a 6-digit code to ${email}.`}
        </p>

        {error ? <div className="mb-4"><ErrorNote error={error} /></div> : null}

        {step === "email" ? (
          <form onSubmit={submitEmail} className="flex flex-col gap-3">
            <Input value={email} onChange={setEmail} type="email" placeholder="you@example.com" />
            <Button type="submit" disabled={busy || !email}>
              {busy ? "Sending…" : "Send code"}
            </Button>
          </form>
        ) : (
          <form onSubmit={submitCode} className="flex flex-col gap-3">
            <Input value={code} onChange={setCode} placeholder="123456" />
            <Button type="submit" disabled={busy || code.length < 6}>
              {busy ? "Checking…" : "Sign in"}
            </Button>
            <button
              type="button"
              className="text-xs text-muted hover:underline"
              onClick={() => {
                setStep("email");
                setCode("");
                setError(null);
              }}
            >
              Use a different email
            </button>
          </form>
        )}
      </Card>
    </div>
  );
}
